import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Container,
  Form,
  Nav,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";

import { useMsal } from "@azure/msal-react";

import useApi from "../../apiProvider";

import AccessDenied from "../../Generic/AccessDenied";
import FormModal from "./Modals/FormModal";
import Icon from "../generic/Icon";

import styles from "../generic/css/Table.module.css";
import "../generic/css/ResponsiveForms.css";

const ReferenceTables = () => {
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entryType, setEntryType] = useState("Category");
  const [newEntry, setNewEntry] = useState({ name: "", description: "" });
  const [errors, setErrors] = useState({
    nameError: "",
    descriptionError: "",
  });

  // Get data for current user
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const roles = activeAccount?.idTokenClaims.roles || [];
  const isAdmin = roles.includes("cvp.write");

  const defaultEntryType = React.useMemo(
    () => ({
      title: "Categories",
      single: "Category",
      camel: "category",
    }),
    [],
  );

  // Define form fields for the modal
  const formFields = [
    {
      name: "name",
      type: "text",
      placeholder: "Name *",
      required: true,
      minLength: 1,
      maxLength: 50,
      errorKey: "nameError",
    },
    {
      name: "description",
      type: "textarea",
      placeholder: "Description",
      maxLength: 1024,
      rows: 4,
      errorKey: "descriptionError",
    },
  ];

  const closeModal = () => {
    setShow(false);
    setNewEntry({ name: "", description: "" });
    setErrors({
      nameError: "",
      descriptionError: "",
    });
  };

  useEffect(() => {
    const getData = async () => {
      setEntryType(defaultEntryType);
      let results = await useApi.getAllCategories();
      console.log("results: ", results);

      setIsLoading(false);
      setData(
        results.sort(
          (a, b) =>
            Number(b.enabled) - Number(a.enabled) ||
            b.count - a.count ||
            a.name.localeCompare(b.name),
        ),
      );
    };
    setIsLoading(true);
    getData();
  }, [defaultEntryType]);

  const saveEntry = async () => {
    if (!newEntry.name?.trim()) {
      setErrors((prev) => ({
        ...prev,
        nameError: "Name Required",
      }));
      return;
    }

    let submitting = {
      name: newEntry.name.trim(),
      description: newEntry.description.trim(),
    };
    let results = await useApi.createNewEntry(entryType.camel, submitting);
    setNewEntry({ name: "", description: "" });
    setData(
      results?.returnData.sort(
        (a, b) =>
          Number(b.enabled) - Number(a.enabled) ||
          b.count - a.count ||
          a.name.localeCompare(b.name),
      ),
    );
    setErrors({
      nameError: "",
      descriptionError: "",
    });
    setShow(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewEntry((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const switchChange = async (row, event) => {
    setIsLoading(true);
    await useApi
      .updateEnabled(entryType.camel, {
        id: row.id,
        enabled: event.target.checked,
      })
      .then(
        setData((prevData) =>
          prevData.map((r) =>
            r.id === row.id ? { ...r, enabled: event.target.checked } : r,
          ),
        ),
      )
      .then(setIsLoading(false));
  };

  const handleNavClick = async (givenType) => {
    setIsLoading(true);
    let results;
    switch (givenType) {
      case "Handoff Types":
        setEntryType({
          title: "Handoff Types",
          single: "Handoff Type",
          camel: "handoffType",
        });
        results = await useApi.getAllHandoffTypes();
        break;
      case "Redundancy Types":
        setEntryType({
          title: "Redundancy Types",
          single: "Redundancy Type",
          camel: "redundancyType",
        });
        results = await useApi.getAllRedundancyTypes();
        break;
      case "Scopes":
        setEntryType({
          title: "Scopes",
          single: "Scope",
          camel: "scope",
        });
        results = await useApi.getAllScopes();
        break;
      case "Status":
        setEntryType({
          title: "Status",
          single: "Status",
          camel: "status",
        });
        results = await useApi.getAllStatus();
        break;
      case "Types":
        setEntryType({
          title: "Types",
          single: "Type",
          camel: "type",
        });
        results = await useApi.getAllTypes();
        break;
      case "Uses":
        setEntryType({
          title: "Uses",
          single: "Use",
          camel: "use",
        });
        results = await useApi.getAllUses();
        break;
      default:
        setEntryType(defaultEntryType);
        results = await useApi.getAllCategories();
        break;
    }
    setIsLoading(false);
    setData(
      results.sort(
        (a, b) =>
          Number(b.enabled) - Number(a.enabled) ||
          b.count - a.count ||
          a.name.localeCompare(b.name),
      ),
    );
  };

  return (
    <>
      {isAdmin ? (
        <>
          <Container fluid className="contentContainer">
            <Row className="justify-content-center">
              <Card className="shadow-sm mb-4 p-0">
                <Card.Body className="m-0 p-0">
                  <Col
                    xs="auto"
                    className="d-flex justify-content-end align-items-end"
                    style={{
                      position: "absolute",
                      top: "15px",
                      right: "50px",
                    }}
                  >
                    <Icon
                      onClick={() => setShow(true)}
                      name="add"
                      overrides={{
                        size: "1.5rem",
                        tooltip: `Add ${entryType.single}`,
                        disabled: !isAdmin,
                      }}
                    />
                  </Col>
                  <Container fluid>
                    <Row>
                      <Col sm={2} className="bg-darkgrey text-black ">
                        <Nav
                          fill
                          className="flex-column ms-2 mt-5 m-2 mb-5"
                          defaultActiveKey="/categories"
                        >
                          <Nav.Link
                            href="/categories"
                            style={{
                              backgroundColor:
                                entryType.title === "Categories" && "#0071cd",
                              color:
                                entryType.title === "Categories"
                                  ? "white"
                                  : "black",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick("Categories");
                            }}
                          >
                            Categories
                          </Nav.Link>
                          <Nav.Link
                            href="/handoff"
                            style={{
                              backgroundColor:
                                entryType.title === "Handoff Types" &&
                                "#0071cd",
                              color:
                                entryType.title === "Handoff Types"
                                  ? "white"
                                  : "black",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick("Handoff Types");
                            }}
                          >
                            Handoff Types
                          </Nav.Link>
                          <Nav.Link
                            href="/redundancy"
                            style={{
                              backgroundColor:
                                entryType.title === "Redundancy Types" &&
                                "#0071cd",
                              color:
                                entryType.title === "Redundancy Types"
                                  ? "white"
                                  : "black",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick("Redundancy Types");
                            }}
                          >
                            Redundancy Types
                          </Nav.Link>
                          <Nav.Link
                            href="/scopes"
                            style={{
                              backgroundColor:
                                entryType.title === "Scopes" && "#0071cd",
                              color:
                                entryType.title === "Scopes"
                                  ? "white"
                                  : "black",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick("Scopes");
                            }}
                          >
                            Scopes
                          </Nav.Link>
                          <Nav.Link
                            href="/status"
                            style={{
                              backgroundColor:
                                entryType.title === "Status" && "#0071cd",
                              color:
                                entryType.title === "Status"
                                  ? "white"
                                  : "black",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick("Status");
                            }}
                          >
                            Status
                          </Nav.Link>
                          <Nav.Link
                            href="/types"
                            style={{
                              backgroundColor:
                                entryType.title === "Types" && "#0071cd",
                              color:
                                entryType.title === "Types" ? "white" : "black",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick("Types");
                            }}
                          >
                            Types
                          </Nav.Link>
                          <Nav.Link
                            href="/uses"
                            style={{
                              backgroundColor:
                                entryType.title === "Uses" && "#0071cd",
                              color:
                                entryType.title === "Uses" ? "white" : "black",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick("Uses");
                            }}
                          >
                            Uses
                          </Nav.Link>
                        </Nav>
                      </Col>

                      <Col sm={10}>
                        <FormModal
                          show={show}
                          title={`Add New ${entryType.single}`}
                          fields={formFields}
                          formData={newEntry}
                          errors={errors}
                          onClose={closeModal}
                          onSave={saveEntry}
                          onChange={handleChange}
                        />
                        {isLoading && (
                          <div className={styles["overlay"]}>
                            <Spinner animation="border" variant="light" />
                          </div>
                        )}
                        <Table
                          striped
                          bordered
                          className={isLoading ? "table-overlay" : ""}
                        >
                          <thead
                            style={{
                              position: "sticky",
                              top: "-1px",
                              backgroundColor: "#0072ce",
                              color: "white",
                            }}
                          >
                            <tr>
                              <th
                                className="ps-3"
                                style={{
                                  whiteSpace: "nowrap",
                                  width: "35%",
                                  textAlign: "left",
                                }}
                              >
                                <DynamicTooltip
                                  title="Name"
                                  tooltipText="Name as it will appear in the dropdown"
                                />
                              </th>

                              <th
                                className="ps-3"
                                style={{
                                  whiteSpace: "nowrap",
                                  width: "35%",
                                  textAlign: "left",
                                }}
                              >
                                <DynamicTooltip
                                  title="Description"
                                  tooltipText="This will be displayed when a user hovers over any name in the dropdown menu"
                                />
                              </th>
                              <th
                                style={{
                                  whiteSpace: "nowrap",
                                  width: "15%",
                                  padding: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <DynamicTooltip
                                    title="Count"
                                    tooltipText="Number of times this reference is used across the application"
                                  />
                                </div>
                              </th>

                              <th
                                style={{
                                  whiteSpace: "nowrap",
                                  width: "15%",
                                  padding: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <DynamicTooltip
                                    title="Enable"
                                    tooltipText="Toggle to determine if this reference appears in the dropdown lists"
                                  />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className={styles["table-body"]}>
                            {data.map((row) => {
                              return (
                                <tr>
                                  <td
                                    style={{
                                      paddingLeft: "15px !important",
                                      wordWrap: "break-word",
                                      textAlign: "left",
                                    }}
                                  >
                                    {row.name}
                                  </td>
                                  <td
                                    className="fs-6 "
                                    style={{
                                      wordWrap: "break-word",
                                      wordBreak: "break-word",
                                      textAlign: "left",
                                      paddingLeft: "15px !important",
                                    }}
                                  >
                                    {row.description}
                                  </td>
                                  <td className="fs-6 ">{row.count}</td>
                                  <td
                                    style={{
                                      textAlign: "center",
                                      verticalAlign: "middle",
                                      padding: 0,
                                      width: "1px",
                                    }}
                                  >
                                    <Form.Check
                                      type="switch"
                                      id="custom-switch"
                                      label=""
                                      checked={row.enabled}
                                      className="custom-switch"
                                      onChange={(event) =>
                                        switchChange(row, event)
                                      }
                                      style={{ display: "inline-block" }}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </Container>
                </Card.Body>
              </Card>
            </Row>
          </Container>
        </>
      ) : (
        <AccessDenied />
      )}
    </>
  );
};

const DynamicTooltip = ({ title, tooltipText, placement = "top" }) => {
  const renderTooltip = (props) => (
    <Tooltip id="dynamic-tooltip" {...props}>
      {tooltipText}
    </Tooltip>
  );

  return (
    <OverlayTrigger placement={placement} overlay={renderTooltip}>
      <span className="tooltip-target">{title}</span>
    </OverlayTrigger>
  );
};

export default ReferenceTables;
